import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { PaymentsService } from "../../../services/Financial/Payments/PaymentsServices";
import { PaymentsModel } from "../../../models/Financial/Payments/PaymentsModel";
import { SubscriptionsService } from "../../../services/Financial/Subscriptions/SubscriptionsServices";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  payments: PaymentsModel[];
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  methodsMap: Record<string, string>;
  statusesMap: Record<string, string>;
  subscriptionsMap: Record<string, any>;
}

export const PaymentsContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface PaymentsProviderProps {
  children: ReactNode;
}

export const PaymentsProvider = ({
  children,
}: PaymentsProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [payments, setPayments] = useState<PaymentsModel[]>([]);
  const [methodsMap, setMethodsMap] = useState<Record<string, string>>({});
  const [statusesMap, setStatusesMap] = useState<Record<string, string>>({});
  const [subscriptionsMap, setSubscriptionsMap] = useState<Record<string, any>>({});

  const paymentsService = useRef(new PaymentsService());
  const subscriptionsService = useRef(new SubscriptionsService());
  const location = useLocation();

  useEffect(() => {
    const loadLookups = async () => {
      const [methodsRes] = await paymentsService.current.listMethods({ withPaginate: false });
      const methods: any[] = methodsRes?.data?.items ?? methodsRes?.data ?? [];
      if (methods.length > 0) {
        const map: Record<string, string> = {};
        methods.forEach((m: any) => { map[String(m.id)] = m.name; });
        setMethodsMap(map);
      }

      const [statusRes] = await paymentsService.current.listStatus({ withPaginate: false });
      const statuses: any[] = statusRes?.data?.items ?? statusRes?.data ?? [];
      if (statuses.length > 0) {
        const map: Record<string, string> = {};
        statuses.forEach((s: any) => { map[String(s.id)] = s.name; });
        setStatusesMap(map);
      }

      const [subsRes] = await subscriptionsService.current.list({ withPaginate: false });
      const subs: any[] = subsRes?.data?.items ?? subsRes?.data ?? [];
      if (subs.length > 0) {
        const map: Record<string, any> = {};
        subs.forEach((s: any) => { map[String(s.id)] = s; });
        setSubscriptionsMap(map);
      }
    };

    loadLookups();
  }, []);

  const handleList = useCallback(async () => {
    if (
      !params ||
      !location.pathname.includes("/financial/payments")
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await paymentsService.current.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setPayments(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os pagamentos do sistema.");
    }
  }, [params, location.pathname]);

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <PaymentsContext.Provider
      value={{
        params,
        setParams,
        payments,
        pages,
        setPages,
        isLoading,
        error,
        setError,
        handleList,
        methodsMap,
        statusesMap,
        subscriptionsMap,
      }}
    >
      {children}
    </PaymentsContext.Provider>
  );
};

export const usePayments = () => useContext(PaymentsContext);
